import React from "react";
import PropTypes from "prop-types";

import { makeStyles, withStyles } from "tss-react/mui";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Accordion,
    AccordionSummary,
} from "@mui/material";
import {
    Typography,
    Paper,
    Container,
    TextField,
    Button,
    MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { userService } from "./services/user_functions";
import { authenticationService } from "./services/auth_service";
import { DateField } from "@mui/x-date-pickers";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    {
        id: "created_at",
        numeric: false,
        disablePadding: false,
        label: "Data Criação",
    },
    { id: "message", numeric: false, disablePadding: false, label: "Mensagem" },
];

function EnhancedTableHead(props) {
    const { classes, order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align="left"
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            <b>{headCell.label}</b>
                            {/* {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === "desc"
                                        ? "sorted descending"
                                        : "sorted ascending"}
                                </span>
                            ) : null}*/}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        width: "100%",
    },
    paper: {
        width: "100%",
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    visuallyHidden: {
        border: 0,
        clip: "rect(0 0 0 0)",
        height: 1,
        margin: -1,
        overflow: "hidden",
        padding: 0,
        position: "absolute",
        top: 20,
        width: 1,
    },
    cell: {
        whiteSpace: "nowrap",
    },
}));

function EnhancedTable(props) {
    const { rows } = props;
    const classes = useStyles();

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("ordering");
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    React.useEffect(() => {
        setPage(0);
    }, [rows]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = rows.map((n) => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    const emptyRows =
        rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <TableContainer>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size="medium"
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage,
                                )
                                .map((row, index) => {
                                    const isItemSelected = isSelected(
                                        row.IDExport,
                                    );

                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) =>
                                                handleClick(event, row.IDExport)
                                            }
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.IDExport}
                                            selected={isItemSelected}
                                        >
                                            <TableCell
                                                className={classes.cell}
                                                align="left"
                                            >
                                                {row.created_at === null
                                                    ? ""
                                                    : new Date(
                                                          row.created_at,
                                                      ).toLocaleDateString(
                                                          "pt-BR",
                                                          { timeZone: "UTC" },
                                                      )}
                                            </TableCell>
                                            <TableCell
                                                className={classes.cell}
                                                align="left"
                                            >
                                                {row.message}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    labelDisplayedRows={({ from, to, count }) =>
                        `Mostrando ${from}-${to} de ${count} itens.`
                    }
                    labelRowsPerPage="Itens por página:"
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </div>
    );
}

const useStyles2 = (theme, props) => ({
    paper: {
        marginTop: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
    },

    grid: {
        margin: "0",
    },
    filtrosHeader: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    table: {
        width: "100%",
        minWidth: 700,
        alignItems: "center",
    },
    tableitem: {
        width: "10%",
    },
    tableitem_m: {
        width: "20%",
    },
    text: {
        width: "200px", // ou '100%' se quiser ocupar o máximo possível do <td>
    },
    wideTextField: {
        width: "300px", // ou '100%' para ocupar todo o espaço do <td>
    },
});

/* FIM DA PARTE IMPORTADA*/

class Log extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentUser: this.props.userData,
            created_at: null,
            log_type_id: "",
            logTypes: [],
            rows: [],
        };
    }

    loadLogTypes = () => {
        userService
            .listLogTypes()
            .then((response) => {
                if (!response.data || !Array.isArray(response.data)) {
                    throw new Error("Resposta da API inválida");
                }

                const options = [
                    ...response.data
                        .map((item) => {
                            if (!item.id) {
                                console.warn("Item sem ID:", item);
                                return null;
                            }

                            return {
                                value: item.id,
                                label: `${item.event_code || "N/A"} - ${item.code || "N/A"} - ${item.description || "N/A"} - ${item.id}`,
                                event_code: item.event_code,
                                rawData: item,
                            };
                        })
                        .filter(Boolean),
                ];
                this.setState({ logTypes: options });
            })
            .catch((error) => {
                console.error("Erro ao carregar tipos:", error);
                // Fallback para não quebrar a interface
                this.setState({
                    logTypes: [
                        { value: "error", label: "Erro ao carregar tipos" },
                    ],
                });
            });
    };

    listLogs = () => {
        // userService.listLogs(this.state.created_at, this.state.message, this.state.log_type_id).then(response => {
        userService
            .listLogs(this.state.created_at, this.state.log_type_id)
            .then((response) => {
                this.setState({ rows: response.data });
            })
            .catch((e) => {
                if (e.response) {
                    if (e.response.status === 400) {
                        window.alert(e.response.data.detail);
                    } else {
                        window.alert(
                            "A sua sessão expirou. Entre com seus dados novamente.",
                        );
                        authenticationService.logout();
                        this.props.history.push("/");
                    }
                } else {
                    window.alert(
                        "A sua sessão expirou. Entre com seus dados novamente.",
                    );
                    authenticationService.logout();
                    this.props.history.push("/");
                }
            });
    };
    limparFiltros = () => {
        this.setState(
            {
                created_at: null,
                log_type_id: "",
            },
            () => {
                this.listLogs();
                this.loadLogTypes();
            },
        );
    };

    componentDidMount() {
        console.log("Iniciando carga de tipos...");
        this.loadLogTypes();
        if (this.props.userData.is_staff && this.props.userData.is_superuser) {
            this.listLogs();
            this.loadLogTypes();
        }
    }

    render() {
        const { classes } = this.props;
        return (
            <Container sx={{ paddingBottom: "20px", marginBottom: "30px" }}>
                <Accordion className={classes.grid}>
                    <AccordionSummary
                        className={classes.filtrosHeader}
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography
                            variant="button"
                            className={classes.heading}
                        >
                            <b>Filtros</b>
                        </Typography>
                    </AccordionSummary>
                    <TableContainer>
                        <table className={classes.table}>
                            <tbody>
                                <tr>
                                    <td
                                        align="right"
                                        className={classes.tableitem}
                                    >
                                        <Typography>
                                            Data de criação:
                                        </Typography>
                                    </td>
                                    {/* <td className={classes.tableitem}><TextField id = 'created_at' type="date" value={this.state.created_at} onChange={e => this.setState({ created_at: (e.target.value === '' ? '' : encodeURIComponent(new Date(e.target.value).toLocaleDateString("pt-BR", {dateStyle: 'short', timeZone: 'UTC' }))) })}/> </td> */}
                                    <td className={classes.tableitem}>
                                        {" "}
                                        <DateField
                                            disableFuture
                                            size="small"
                                            format="DD/MM/YYYY"
                                            value={this.state.created_at}
                                            onChange={(newValue) =>
                                                this.setState({
                                                    created_at: newValue,
                                                })
                                            }
                                        />{" "}
                                    </td>
                                    <td
                                        align="left"
                                        className={classes.tableitem}
                                    >
                                        <Typography>Tipo Log:</Typography>
                                    </td>
                                    <td className={classes.tableitem}>
                                        <TextField
                                            size="small"
                                            select
                                            variant="outlined"
                                            required
                                            fullWidth
                                            id="log_type_id"
                                            value={
                                                this.state.log_type_id
                                                    ? this.state.log_type_id
                                                    : ""
                                            }
                                            className={classes.text}
                                            onChange={(e) => {
                                                this.setState({
                                                    log_type_id: e.target.value,
                                                });
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Selecione um tipo</em>
                                            </MenuItem>
                                            {this.state.logTypes.map(
                                                (option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </TextField>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan="0" align="right">
                                        <Button
                                            variant="contained"
                                            onClick={this.listLogs}
                                        >
                                            FILTRAR
                                        </Button>
                                    </td>
                                    <td colSpan="2" align="right">
                                        <Button
                                            variant="contained"
                                            onClick={this.limparFiltros}
                                        >
                                            LIMPAR BUSCA
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </TableContainer>
                </Accordion>
                <EnhancedTable rows={this.state.rows} />
            </Container>
        );
    }
}

export default withStyles(Log, useStyles2);
